import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { skip, switchMap, tap } from 'rxjs';

import { TranslateModule } from '@ngx-translate/core';
import { MatrixGenericTableComponent } from '../../../../shared/components/generic-table/generic-table.component';
import type {
  GenericTableCellContext,
  GenericTableColumn,
} from '../../../../shared/components/generic-table/models/table-column.model';
import type { TableQuery } from '../../../../shared/components/generic-table/models/table-query.model';
import { MatrixCardComponent } from '../../../../shared/components/matrix-card/matrix-card.component';
import type { MatrixInvestigationRow } from '../../models/matrix-investigation.model';
import { DASHBOARD_DATA } from '../../services/dashboard-data.token';
import { employeeStatusIconsGenericTableConfig } from '../../../../shared/constants/employee-status-icons.constants';

@Component({
  selector: 'app-matrix-investigation-card',
  standalone: true,
  imports: [CommonModule, MatrixGenericTableComponent, MatrixCardComponent, TranslateModule],
  templateUrl: './matrix-investigation-card.component.html',
})
export class MatrixInvestigationCardComponent implements OnInit {
  @Input() highlighted = false;
  readonly externalFilters = input<Record<string, string[]> | null>(null);

  private readonly dashboardData = inject(DASHBOARD_DATA);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('employeeCell', { static: true })
  private readonly employeeCellTpl!: TemplateRef<GenericTableCellContext<MatrixInvestigationRow>>;

  @ViewChild('statusHeaderTpl', { static: true })
  private readonly statusHeaderTpl!: TemplateRef<{
    col: GenericTableColumn<MatrixInvestigationRow>;
  }>;

  @ViewChild('dateHeaderTpl', { static: true })
  private readonly dateHeaderTpl!: TemplateRef<{ col: GenericTableColumn<MatrixInvestigationRow> }>;

  @ViewChild('timeHeaderTpl', { static: true })
  private readonly timeHeaderTpl!: TemplateRef<{ col: GenericTableColumn<MatrixInvestigationRow> }>;

  readonly columns = signal<GenericTableColumn<MatrixInvestigationRow>[]>([]);

  // table state
  readonly data = signal<MatrixInvestigationRow[]>([]);
  readonly totalRecords = signal(0);
  readonly loading = signal(false);
  readonly rows = signal(10);
  readonly scrollHeight = '240px';

  private readonly querySig = signal<TableQuery>({ page: 0, rows: this.rows() });

  constructor() {
    toObservable(this.querySig)
      .pipe(
        tap((q) => {
          this.rows.set(q.rows);
          this.loading.set(true);
        }),
        switchMap((q) => this.dashboardData.getPatientInvestigation(q)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          const rows = Array.isArray(res) ? res : [];
          this.data.set([...rows]);
          this.totalRecords.set(rows.length);
          this.loading.set(false);
        },
        error: (error) => {
          console.error(error);
          this.loading.set(false);
        },
      });

    toObservable(this.externalFilters)
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((filters) => {
        this.querySig.set({
          page: 0,
          rows: this.rows(),
          multiFilters: filters ?? {},
        });
      });
  }

  ngOnInit(): void {
    this.columns.set([
      {
        field: 'managedEmployee',
        header: 'Managed Employee',
        sortable: true,
        filterable: true,
        minWidth: '200px',
        width: '240px',
        cellTemplate: this.employeeCellTpl,
      },
      {
        field: 'investigatorName',
        header: 'Investigator',
        sortable: true,
        filterable: true,
        minWidth: '110px',
        width: '150px',
      },
      {
        field: 'caseDate',
        header: 'Date',
        sortable: true,
        filterable: true,
        minWidth: '120px',
        width: '130px',
        headerTemplate: this.dateHeaderTpl,
      },
      {
        field: 'caseTime',
        header: 'Time',
        sortable: true,
        filterable: true,
        minWidth: '120px',
        width: '130px',
        headerTemplate: this.timeHeaderTpl,
      },
      {
        field: 'employeeStatus',
        header: 'Employee Status',
        sortable: true,
        filterable: true,
        noWrap: true,
        minWidth: '160px',
        width: '200px',
        type: 'status',
        status: employeeStatusIconsGenericTableConfig<MatrixInvestigationRow>(
          (row) => row.employeeStatus,
        ),
        headerTemplate: this.statusHeaderTpl,
      },
    ]);
  }

  onQueryChange(query: TableQuery): void {
    this.querySig.set(query);
  }
}
